import { useAuth } from '../../contexts/AppContext';
import ImageUploader from '../../components/utils/ImageUploader';
import VideoUploader from '../../components/utils/VideoUploader';
import { DeleteOutlined } from '@ant-design/icons';
import { hasElevatedSessionAccess } from '../../utils/sessionPermissions';

/**
 * MediaTab —— 场馆媒体信息管理
 */
const MediaTab = ({ formData, onNested }) => {
  const { token, userInfo } = useAuth();
  if (!formData) return <p>Loading...</p>;

  const landing = formData.landing || {};
  const canEditBottom = hasElevatedSessionAccess(userInfo, formData.permissionLevel);
  const hasLandingBanner =
    !!formData.landingBanner && formData.landingBanner.trim() !== '';


  /** 自动生成 user library 的 prefix / thumbPrefix。
   *  - 如果没有 user.S3Id → 认为是 icon 模式（ImageUploader 自动判断）
   *  - 如果有 user.S3Id → user library 模式（需要裁剪+上传）
   */
  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;

  return (
    <div className="space-y-6 max-h-[calc(100vh-15rem)] overflow-y-auto">
      <section className="bg-white border border-gray-200 p-10 shadow">
        <div className="space-y-10">

          {/* 场馆 Logo */}
          <div>
            <label className="block font-medium text-gray-700 mb-1 text-[1.2rem]">
              Venue Logo
            </label>

            <ImageUploader
              token={token}
              prefix={prefix}
              thumbPrefix={thumbPrefix}
              width={1080}
              height={340}
              value={landing.venueLogo}
              onChange={(url) => onNested('landing', 'venueLogo', url)}
            />

            {landing.venueLogo && (
              <div className="mt-3">
                <img
                  src={landing.venueLogo}
                  alt="Venue Logo"
                  className="max-w-[50rem] mx-auto h-auto rounded mb-2"
                />
              </div>
            )}
          </div>

          {/* Showcase Banner */}
          <div>
            <label className="block font-medium text-gray-700 mb-1 text-[1.2rem]">
              Showcase Banner
            </label>

            <ImageUploader
              token={token}
              prefix={prefix}

              thumbPrefix={thumbPrefix}
              width={1080}
              height={340}
              onChange={(url) => {
                const newSlides = [...(landing.venueSlides || []), url];
                onNested('landing', 'venueSlides', newSlides);
              }}
            />

            {Array.isArray(landing.venueSlides) && landing.venueSlides.length > 0 && (
              <div className="space-y-4 mt-4">
                {landing.venueSlides.map((url, index) => {
                  // First slide is the welcome banner: it can be replaced, but stays first and can't be removed.
                  const isBanner = hasLandingBanner && index === 0;

                  return (
                    <div
                      key={url + index}
                      className="grid grid-cols-[1fr_7rem_7rem] items-center gap-4 bg-white border border-gray-300 rounded-lg shadow px-4 py-2"
                    >
                      <div className="space-y-2">
                        <div className="aspect-[16/5] w-[50rem] bg-gray-50 rounded overflow-hidden relative">
                          <img src={url} alt={`Slide ${index}`} className="w-full h-full object-contain" />
                        </div>

                        {isBanner && (
                          <ImageUploader
                            token={token}
                            prefix={prefix}
                            thumbPrefix={thumbPrefix}
                            width={1080}
                            height={340}
                            buttonText="Change Welcome Banner"
                            onChange={(newUrl) => {
                              onNested('landing', 'welcomeBanner', newUrl);
                              onNested('landing', 'venueSlides', [newUrl, ...landing.venueSlides.slice(1)]);
                            }}
                          />
                        )}
                      </div>

                      {/* 排序按钮 */}
                      <div className="flex flex-col items-center space-y-2">
                        <button
                          disabled={index === 0 || isBanner || (hasLandingBanner && index === 1)}
                          onClick={() => {
                            const newList = [...landing.venueSlides];
                            [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
                            onNested('landing', 'venueSlides', newList);
                          }}
                          className="text-white text-md border bg-blue-400 rounded w-full px-2 py-1 hover:opacity-90 disabled:opacity-30"
                        >
                          Up
                        </button>

                        <button
                          disabled={isBanner || index === landing.venueSlides.length - 1}
                          onClick={() => {
                            const newList = [...landing.venueSlides];
                            [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
                            onNested('landing', 'venueSlides', newList);
                          }}
                          className="text-white text-md border rounded w-full bg-blue-400 px-2 py-1 hover:opacity-90 disabled:opacity-30"
                        >
                          Down
                        </button>
                      </div>

                      {/* 删除 */}
                      <div className="flex justify-end">
                        <button
                          disabled={isBanner}
                          onClick={() => {
                            const newList = landing.venueSlides.filter((_, i) => i !== index);
                            onNested('landing', 'venueSlides', newList);
                          }}
                          className={`text-sm rounded px-4 py-1 transition ${isBanner
                              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                              : 'text-gray-600 bg-white hover:text-white hover:bg-red-500'
                            }`}
                        >
                          <DeleteOutlined style={{ fontSize: '1.5rem' }} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom Slides */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block font-medium text-gray-700 mb-1 text-[1.2rem]">
                Bottom Slides
              </label>
              {!canEditBottom && <span className="text-xs text-gray-500">No Permission</span>}
            </div>

            {canEditBottom && (
              <ImageUploader
                token={token}

                prefix={prefix}
                thumbPrefix={thumbPrefix}
                width={1080}
                height={340}
                onChange={(url) => {
                  const newSlides = [...(landing.bottomSlides || []), url];
                  onNested('landing', 'bottomSlides', newSlides);
                }}
              />
            )}

            {landing.bottomSlides?.length > 0 ? (
              <div className="space-y-4 mt-4">
                {landing.bottomSlides.map((url, index) => (
                  <div
                    key={url + index}
                    className="grid grid-cols-[1fr_7rem_7rem] items-center gap-4 bg-white border border-gray-300 rounded-lg shadow px-4 py-2"
                  >
                    <div className="aspect-[16/5] w-[50rem] bg-gray-50 rounded overflow-hidden">
                      <img src={url} alt={`Bottom Slide ${index}`} className="w-full h-full object-contain" />
                    </div>

                    <div className="flex flex-col items-center space-y-2">
                      {canEditBottom ? (
                        <>
                          <button
                            disabled={index === 0}
                            onClick={() => {
                              const newList = [...landing.bottomSlides];
                              [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
                              onNested('landing', 'bottomSlides', newList);
                            }}
                            className="text-white text-md border bg-blue-400 rounded w-full px-2 py-1 hover:opacity-90 disabled:opacity-30"
                          >
                            Up
                          </button>

                          <button
                            disabled={index === landing.bottomSlides.length - 1}
                            onClick={() => {
                              const newList = [...landing.bottomSlides];
                              [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
                              onNested('landing', 'bottomSlides', newList);
                            }}
                            className="text-white text-md border rounded w-full bg-blue-400 px-2 py-1 hover:opacity-90 disabled:opacity-30"
                          >
                            Down
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="text-xs text-gray-400">—</div>
                          <div className="text-xs text-gray-400">—</div>
                        </>
                      )}
                    </div>

                    <div className="flex justify-end">
                      {canEditBottom ? (
                        <button
                          onClick={() => {
                            const newList = landing.bottomSlides.filter((_, i) => i !== index);
                            onNested('landing', 'bottomSlides', newList);
                          }}
                          className="text-sm text-gray-600 rounded px-4 py-1 bg-white hover:text-white hover:bg-red-500 transition"
                        >
                          <DeleteOutlined style={{ fontSize: '1.5rem' }} />
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 text-sm text-gray-500 italic">No bottom slides</div>
            )}
          </div>

          {/* 视频上传 */}
          <div>
            <label className="block font-medium text-gray-700 mb-1 text-[1.2rem]">
              Venue Videos
            </label>

            <VideoUploader
              token={token}
              onUploaded={(url) => {
                const safeUrl = typeof url === 'string' ? url : String(url);
                const newVideo = [...(landing.venueVideos || []), safeUrl];
                onNested('landing', 'venueVideos', newVideo);
              }}
            />

            {Array.isArray(landing.venueVideos) && landing.venueVideos.length > 0 && (
              <div className="space-y-4 mt-4">
                {landing.venueVideos.map((url, index) => (
                  <div
                    key={url + index}
                    className="grid grid-cols-[1fr_7rem_7rem] items-center gap-4 bg-white border border-gray-300 rounded-lg shadow px-4 py-2"
                  >
                    <div className="w-[30rem] bg-black rounded overflow-hidden">
                      <video src={url} controls className="w-full h-full object-contain" />
                    </div>

                    <div className="flex flex-col items-center space-y-2">
                      <button
                        disabled={index === 0}
                        onClick={() => {
                          const newList = [...landing.venueVideos];
                          [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
                          onNested('landing', 'venueVideos', newList);
                        }}
                        className="text-white text-md border bg-blue-400 rounded w-full px-2 py-1 hover:opacity-90 disabled:opacity-30"
                      >
                        Up
                      </button>

                      <button
                        disabled={index === landing.venueVideos.length - 1}
                        onCreate={() => { }}
                        onClick={() => {
                          const newList = [...landing.venueVideos];
                          [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
                          onNested('landing', 'venueVideos', newList);
                        }}
                        className="text-white text-md border rounded w-full bg-blue-400 px-2 py-1 hover:opacity-90 disabled:opacity-30"
                      >
                        Down
                      </button>
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => {
                          const newList = landing.venueVideos.filter((_, i) => i !== index);
                          onNested('landing', 'venueVideos', newList);
                        }}
                        className="text-sm text-gray-600 rounded px-4 py-1 bg-white hover:text-white hover:bg-red-500 transition"
                      >
                        <DeleteOutlined style={{ fontSize: '1.5rem' }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </section>
    </div>
  );
};

export default MediaTab;
